import { IconBell, IconChartLine, IconRefresh } from "@tabler/icons-react";
import {
	Button,
	Card,
	Col,
	Flex,
	Row,
	Space,
	Statistic,
	Tooltip,
	Typography,
	theme,
} from "antd";
import { formatDistanceToNow } from "date-fns";
import {
	type Deployment,
	isHealthSignal,
	type Signal,
	SignalKind,
	SignalState,
	type SignalStatus,
} from "@/common/model";
import { BaseContainer, CustomEmpty, CustomIcon } from "@/components/base";
import { CustomList } from "@/components/custom-list";
import { LoadingBar } from "@/components/loading-bar";
import {
	SIGNAL_STATE_ORDER,
	SignalStateBadge,
	SignalStateLabelMap,
} from "./signal-badges";
import { formatMetric } from "./signal-format";

type Statuses = Record<string, SignalStatus> | undefined;

/** Worst first; an informational metric (no thresholds, so no verdict) sorts after everything. */
const rankOf = (signal: Signal, statuses: Statuses) => {
	const state = statuses?.[signal.id]?.state;
	return state && isHealthSignal(signal)
		? SIGNAL_STATE_ORDER.indexOf(state)
		: SIGNAL_STATE_ORDER.length;
};

/** Signals sorted worst first, then by name. */
function sortSignals(signals: Signal[], statuses: Statuses) {
	return [...signals].sort(
		(a, b) =>
			rankOf(a, statuses) - rankOf(b, statuses) || a.name.localeCompare(b.name),
	);
}

/** How many health signals are in each state. Informational metrics aren't a verdict, so don't count. */
function countByState(signals: Signal[], statuses: Statuses) {
	const counts = Object.fromEntries(
		SIGNAL_STATE_ORDER.map((s) => [s, 0]),
	) as Record<SignalState, number>;
	for (const signal of signals.filter(isHealthSignal)) {
		const state = statuses?.[signal.id]?.state;
		if (state) counts[state] += 1;
	}
	return counts;
}

/** When the oldest status shown was read, since statuses are cached briefly server-side. */
function oldestObservedAt(statuses: Statuses) {
	const times = Object.values(statuses ?? {}).map((s) =>
		new Date(s.observedAt as string).getTime(),
	);
	return times.length ? new Date(Math.min(...times)) : undefined;
}

/**
 * The project's health at a glance: how many alarms and thresholded metrics are firing, at warning,
 * without data, OK, or could not be read. Shown above the project details when the project has any.
 */
export function SignalSummary({
	signals,
	statuses,
	isFetching,
	onRefresh,
}: {
	signals: Signal[];
	statuses: Statuses;
	isFetching: boolean;
	onRefresh: () => void;
}) {
	const { token } = theme.useToken();
	const counts = countByState(signals, statuses);
	const checkedAt = oldestObservedAt(statuses);
	const colors: Record<SignalState, string | undefined> = {
		[SignalState.FIRING]: token.colorError,
		[SignalState.WARNING]: token.colorWarningText,
		[SignalState.NO_DATA]: undefined,
		[SignalState.UNKNOWN]: undefined,
		[SignalState.OK]: token.colorSuccess,
	};

	return (
		<BaseContainer
			title="Signals"
			actions={
				<Space>
					{checkedAt && (
						<Typography.Text type="secondary">
							Checked {formatDistanceToNow(checkedAt, { addSuffix: true })}
						</Typography.Text>
					)}
					<Tooltip title="Check again">
						<Button
							type="text"
							loading={isFetching}
							icon={<CustomIcon icon={IconRefresh} size={18} />}
							onClick={onRefresh}
						/>
					</Tooltip>
				</Space>
			}
		>
			<Row gutter={[12, 12]}>
				{SIGNAL_STATE_ORDER.map((state) => (
					<Col key={state} flex="1 1 140px">
						<Card
							size="small"
							style={
								state === SignalState.FIRING && counts[state] > 0
									? {
											borderColor: token.colorErrorBorder,
											background: token.colorErrorBg,
										}
									: undefined
							}
						>
							<Statistic
								title={SignalStateLabelMap[state]}
								value={statuses ? counts[state] : "–"}
								// Colour only what's there, so an empty "Firing" doesn't read as an alert.
								styles={{
									content: {
										color: counts[state] > 0 ? colors[state] : undefined,
									},
								}}
							/>
						</Card>
					</Col>
				))}
			</Row>
		</BaseContainer>
	);
}

/** One row's second line: the source's reason, or why the status could not be read. */
function statusDetail(status?: SignalStatus) {
	if (!status) return undefined;
	return status.error ?? status.reason ?? undefined;
}

/**
 * A list of signals, worst first: kind icon, name and detail, then the value and state on the
 * right. Used per group on the project page and in the deployment drawer.
 */
export function SignalRows({
	signals,
	statuses,
	isFetching = false,
	onOpen,
	onAdd,
}: {
	signals: Signal[];
	statuses: Statuses;
	/** True while statuses are being re-read; the list shows a progress bar along its bottom. */
	isFetching?: boolean;
	onOpen: (signal: Signal) => void;
	onAdd?: () => void;
}) {
	return (
		<div style={{ position: "relative" }}>
			<CustomList
				data={sortSignals(signals, statuses)}
				emptyText="signals"
				onAdd={onAdd}
				onItemClick={onOpen}
				renderLeading={(s) => (
					<CustomIcon
						icon={s.kind === SignalKind.METRIC ? IconChartLine : IconBell}
					/>
				)}
				renderItem={(s) => {
					const status = statuses?.[s.id];
					const detail = statusDetail(status);
					return (
						<Flex vertical style={{ minWidth: 0 }}>
							<Typography.Text ellipsis={{ tooltip: s.name }}>
								{s.name}
							</Typography.Text>
							{detail && (
								<Typography.Text
									type="secondary"
									style={{ fontSize: 12 }}
									ellipsis={{ tooltip: detail }}
								>
									{detail}
								</Typography.Text>
							)}
						</Flex>
					);
				}}
				renderTrailing={(s) => {
					const status = statuses?.[s.id];
					return (
						<>
							{s.kind === SignalKind.METRIC && status && (
								<Typography.Text strong>
									{formatMetric(status.value, status.unit)}
								</Typography.Text>
							)}
							{isHealthSignal(s) && (
								<SignalStateBadge state={status?.state as SignalState} />
							)}
						</>
					);
				}}
			/>
			<LoadingBar active={isFetching} />
		</div>
	);
}

/**
 * Every signal on the project, grouped by the deployment it is scoped to, with project-level
 * signals last. Groups with something firing come first.
 */
export function SignalList({
	signals,
	statuses,
	isFetching,
	deployments,
	onOpen,
	onAdd,
}: {
	signals: Signal[];
	statuses: Statuses;
	isFetching?: boolean;
	deployments: Deployment[];
	onOpen: (signal: Signal) => void;
	onAdd: () => void;
}) {
	if (signals.length === 0) {
		return <CustomEmpty description="Signals" onAdd={onAdd} />;
	}

	const groups = [
		...deployments.map((d) => ({
			key: d.id,
			// Deployments are often named after their environment; say which this is.
			title: `Deployment: "${d.name}"`,
			signals: signals.filter((s) => s.deploymentId === d.id),
		})),
		{
			key: "project",
			title: "Project",
			signals: signals.filter((s) => !s.deploymentId),
		},
	]
		.filter((g) => g.signals.length > 0)
		.map((g, index) => ({
			...g,
			index,
			worst: Math.min(...g.signals.map((s) => rankOf(s, statuses))),
		}))
		// Deployment order, except that groups with something wrong rise to the top. The
		// project-level group stays last among equals.
		.sort((a, b) => a.worst - b.worst || a.index - b.index);

	return (
		<Space vertical style={{ width: "100%" }} size="large">
			{groups.map((g) => (
				<BaseContainer key={g.key} size="small" title={g.title}>
					<SignalRows
						signals={g.signals}
						statuses={statuses}
						isFetching={isFetching}
						onOpen={onOpen}
					/>
				</BaseContainer>
			))}
		</Space>
	);
}
